import { Dialog } from "@dash/dialog";
import { Button, DateTimeField } from "@dash/form";
import { useTheme } from "@emotion/react";
import React, { useState } from "react";
import { MdClose } from "react-icons/md";

type DateRange = {
  startDate?: Date;
  endDate?: Date;
};

type Props = {
  label?: string;
  startDate?: Date;
  endDate?: Date;
  onChange: (range: DateRange) => void;
  className?: string;
  isDisabled?: boolean;
  variant?: "primary" | "secondary";
  withTime?: boolean;
};

export const DateRangeFilter: React.FC<Props> = ({
  label,
  startDate,
  endDate,
  onChange,
  className,
  isDisabled,
  withTime,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const formatShort = (date: Date) =>
    `${date.getDate().toString().padStart(2, "0")}.${(date.getMonth() + 1).toString().padStart(2, "0")}`;

  const displayLabel = (() => {
    if (startDate && endDate) return `${formatShort(startDate)} – ${formatShort(endDate)}`;
    if (startDate) return `${formatShort(startDate)} – x`;
    if (endDate) return `x – ${formatShort(endDate)}`;
    return label ?? "Wybierz daty";
  })();

  const handleChange = (key: "startDate" | "endDate", newValue: Date | undefined) => {
    onChange({
      startDate: key === "startDate" ? newValue : startDate,
      endDate: key === "endDate" ? newValue : endDate,
    });
  };

  const reset = () => onChange({ startDate: undefined, endDate: undefined });
  const isActive = startDate || endDate;

  return (
    <>
      <div
        className={className}
        css={{
          cursor: isDisabled ? "not-allowed" : "pointer",
          display: "inline-flex",
          alignItems: "center",
          fontWeight: isActive ? 700 : 400,
          color: isActive ? "white" : theme.colors.primary,
          background: isActive ? theme.colors.primary : theme.colors.background,
          border: `1px solid ${isActive ? "transparent" : theme.colors.primary}`,
          borderRadius: theme.smallBorderRadius,
          fontSize: "0.875rem",
          whiteSpace: "nowrap",
        }}
        onClick={isDisabled ? undefined : () => setOpen(true)}
      >
        <div css={{ padding: "5px 10px" }}>{displayLabel}</div>
        {(startDate || endDate) && (
          <button
            css={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
              color: "white",
              padding: "0.25rem",
              alignSelf: "stretch",
              backgroundColor: "rgba(255,255,255,0.15)",
              margin: 0,
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              reset();
            }}
          >
            <MdClose size={14} />
          </button>
        )}
      </div>

      {open && (
        <Dialog onClose={() => setOpen(false)} header="Zakres dat" css={{ width: "400px" }}>
          <div css={{ display: "flex", flexDirection: "column", gap: "1.25rem", paddingTop: "0.5rem" }}>
            <DateTimeField
              label="Data początkowa"
              value={startDate}
              onChange={(date) => handleChange("startDate", date)}
              showTime={withTime}
              maxDate={endDate}
            />

            <DateTimeField
              label="Data końcowa"
              value={endDate}
              onChange={(date) => handleChange("endDate", date)}
              showTime={withTime}
              minDate={startDate}
            />

            <div css={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1rem" }}>
              <Button variant="primary" onClick={() => setOpen(false)}>
                Zastosuj
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
};
